import { errorToastClassName, successToastClassName, appToast as toast } from "@/components/ui/baseUi/sonner"
import { boxSlotSessionKeys } from "@/modules/boxSlotSession/gateway/boxSlotSessionKey"
import {
  useBoxSessionWeekCopy,
  useBoxSessionWeekCopyActions,
} from "@/modules/boxSlotSession/store/useBoxSessionWeekCopyStore"
import { useServicesContext } from "@/modules/core/services/services.context"
import { useCurrentBox } from "@modules/box/hooks/useBox"
import { word } from "@modules/core/utils/i18n"
import { useLanguage } from "@modules/language/hooks/useLanguage"
import { verboseDateIntervalFormat } from "@modules/utils/dateUtils"
import { useQueryClient } from "@tanstack/react-query"
import dayjs from "dayjs"

export const useBoxSlotDuplicateSession = () => {
  const { result: currentBox } = useCurrentBox()
  const language = useLanguage()
  const weekToCopy = useBoxSessionWeekCopy()
  const { setWeekToCopy, clearWeekToCopy } = useBoxSessionWeekCopyActions()
  const { boxSlotApi } = useServicesContext()
  const queryClient = useQueryClient()

  const onCopyBoxProgrammationSessionWeek = (
    boxProgramIdToCopy: string,
    boxProgramName: string,
    startDateToCopy: Date,
    endDateToCopy: Date,
  ) => {
    if (currentBox)
      setWeekToCopy({
        boxId: currentBox.id,
        boxName: currentBox.name,
        boxProgramId: boxProgramIdToCopy,
        boxProgramName: boxProgramName,
        startDate: startDateToCopy,
        endDate: endDateToCopy,
      })

    toast.success(word("program.weekCopyPaste.copy.success"), {
      classNames: successToastClassName(),
    })
  }

  const onPasteBoxProgrammationSessionWeek = async (
    destinationProgramId: string,
    destinationStartDate: Date,
    overwrite?: boolean,
  ) => {
    if (weekToCopy) {
      try {
        await boxSlotApi.duplicateWeekSessions(
          weekToCopy.boxProgramId,
          destinationProgramId,
          weekToCopy.startDate,
          weekToCopy.endDate,
          destinationStartDate,
          overwrite ?? false,
        )

        const formattedDates = verboseDateIntervalFormat(
          dayjs(weekToCopy.startDate),
          dayjs(weekToCopy.endDate),
          language,
        )

        toast.success(word("program.weekCopyPaste.paste.success", { formattedDates }), {
          classNames: successToastClassName(),
        })

        clearWeekToCopy()
        queryClient.invalidateQueries({ queryKey: boxSlotSessionKeys.all })
      } catch (e: unknown) {
        toast.error(word("program.weekCopyPaste.paste.error"), {
          description: e instanceof Error ? e.message : String(e),
          classNames: errorToastClassName(),
        })
      }
    }
  }

  return {
    onCopyBoxProgrammationSessionWeek,
    onPasteBoxProgrammationSessionWeek,
  }
}
